import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useAuthPrompt } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  TrendingUp,
  TrendingDown,
  ArrowUpRight,
  ArrowDownRight,
  Search,
  Filter,
  Loader2,
  Briefcase,
  User,
  BarChart3,
  PiggyBank,
  Layers,
  Calendar,
  DollarSign,
  ChevronRight
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

export default function MergedView() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { showAuthPrompt } = useAuthPrompt();
  
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'business', 'personal'
  const [searchTerm, setSearchTerm] = useState('');
  const [period, setPeriod] = useState('month');
  
  const [businessData, setBusinessData] = useState({ income: 0, expenses: 0, transactions: [] });
  const [personalData, setPersonalData] = useState({ income: 0, expenses: 0, transactions: [] });

  useEffect(() => {
    fetchAllData();
  }, [period]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const [businessRes, personalRes] = await Promise.all([
        axios.get(`${API_URL}/api/business/expense-tracker/summary?period=${period}`, { headers }),
        axios.get(`${API_URL}/api/personal/expense-tracker/summary?period=${period}`, { headers })
      ]);
      
      setBusinessData({
        income: businessRes.data.total_income || 0,
        expenses: businessRes.data.total_expenses || 0,
        transactions: (businessRes.data.recent_transactions || []).map(t => ({ ...t, context: 'business' }))
      });
      
      setPersonalData({
        income: personalRes.data.total_income || 0,
        expenses: personalRes.data.total_expenses || 0,
        transactions: (personalRes.data.recent_transactions || []).map(t => ({ ...t, context: 'personal' }))
      });
    } catch (error) {
      // Demo data
      setBusinessData({
        income: 6250,
        expenses: 1419,
        transactions: [
          { id: '1', description: 'Client Payment', amount: 2500, date: '2025-02-24', category: 'Consulting', context: 'business', entry_type: 'income' },
          { id: '2', description: 'Software Subscription', amount: -99, date: '2025-02-23', category: 'Software', context: 'business', entry_type: 'expense' },
          { id: '3', description: 'Office Supplies', amount: -150, date: '2025-02-22', category: 'Office', context: 'business', entry_type: 'expense' }
        ]
      });
      setPersonalData({
        income: 3500,
        expenses: 1850,
        transactions: [
          { id: '4', description: 'Salary Deposit', amount: 3500, date: '2025-02-24', category: 'Salary', context: 'personal', entry_type: 'income' },
          { id: '5', description: 'Grocery Store', amount: -125, date: '2025-02-23', category: 'Food & Dining', context: 'personal', entry_type: 'expense' },
          { id: '6', description: 'Netflix', amount: -15.99, date: '2025-02-22', category: 'Streaming', context: 'personal', entry_type: 'expense' }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  // Calculate combined totals
  const combinedIncome = businessData.income + personalData.income;
  const combinedExpenses = businessData.expenses + personalData.expenses;
  const combinedNet = combinedIncome - combinedExpenses;
  
  // Filter transactions
  const allTransactions = [...businessData.transactions, ...personalData.transactions]
    .sort((a, b) => new Date(b.date) - new Date(a.date));
  
  const filteredTransactions = allTransactions.filter(txn => {
    const matchesFilter = filter === 'all' || txn.context === filter;
    const matchesSearch = !searchTerm || 
      (txn.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (txn.category || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const periodLabel = period === 'month' ? 'This Month' : period === 'quarter' ? 'This Quarter' : 'This Year';

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--accent))]" />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="merged-view">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Space_Grotesk']">
            Combined <span className="text-[hsl(var(--accent))]">Overview</span>
          </h1>
          <p className="text-muted-foreground mt-1">See your complete financial picture across all accounts</p>
        </div>
        
        {/* Period Toggle */}
        <div className="flex bg-muted rounded-lg p-1">
          {['month', 'quarter', 'year'].map((p) => (
            <button
              key={p}
              onClick={() => setPeriod(p)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all capitalize ${
                period === p ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Combined Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="card-premium border-2 border-[hsl(var(--accent))]/30">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Combined Income</p>
                <p className="text-2xl font-bold text-emerald-500">{formatCurrency(combinedIncome)}</p>
                <p className="text-xs text-muted-foreground mt-1">{periodLabel}</p>
              </div>
              <div className="p-3 rounded-xl bg-emerald-500/10">
                <TrendingUp className="w-6 h-6 text-emerald-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-premium border-2 border-[hsl(var(--accent))]/30">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Combined Expenses</p>
                <p className="text-2xl font-bold text-red-500">{formatCurrency(combinedExpenses)}</p>
                <p className="text-xs text-muted-foreground mt-1">{periodLabel}</p>
              </div>
              <div className="p-3 rounded-xl bg-red-500/10">
                <TrendingDown className="w-6 h-6 text-red-500" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-premium border-2 border-[hsl(var(--accent))]/30">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Net Position</p>
                <p className={`text-2xl font-bold ${combinedNet >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                  {formatCurrency(combinedNet)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">{periodLabel}</p>
              </div>
              <div className={`p-3 rounded-xl ${combinedNet >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                <PiggyBank className={`w-6 h-6 ${combinedNet >= 0 ? 'text-emerald-500' : 'text-red-500'}`} />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-premium border-2 border-[hsl(var(--accent))]/30">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Transactions</p>
                <p className="text-2xl font-bold text-[hsl(var(--accent))]">{allTransactions.length}</p>
                <p className="text-xs text-muted-foreground mt-1">{periodLabel}</p>
              </div>
              <div className="p-3 rounded-xl bg-[hsl(var(--accent))]/10">
                <Layers className="w-6 h-6 text-[hsl(var(--accent))]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Breakdown Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Business Summary */}
        <Card className="card-premium cursor-pointer hover:border-emerald-500/50 transition-all" onClick={() => navigate('/business/dashboard')}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-emerald-500/10">
                  <Briefcase className="w-5 h-5 text-emerald-500" />
                </div>
                Business
              </CardTitle>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xs text-muted-foreground">Income</p>
                <p className="text-lg font-bold text-emerald-500">{formatCurrency(businessData.income)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Expenses</p>
                <p className="text-lg font-bold text-red-500">{formatCurrency(businessData.expenses)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Net</p>
                <p className={`text-lg font-bold ${businessData.income - businessData.expenses >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                  {formatCurrency(businessData.income - businessData.expenses)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Personal Summary */}
        <Card className="card-premium cursor-pointer hover:border-blue-500/50 transition-all" onClick={() => navigate('/personal/dashboard')}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <div className="p-2 rounded-lg bg-blue-500/10">
                  <User className="w-5 h-5 text-blue-500" />
                </div>
                Personal
              </CardTitle>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xs text-muted-foreground">Income</p>
                <p className="text-lg font-bold text-emerald-500">{formatCurrency(personalData.income)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Expenses</p>
                <p className="text-lg font-bold text-red-500">{formatCurrency(personalData.expenses)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Net</p>
                <p className={`text-lg font-bold ${personalData.income - personalData.expenses >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                  {formatCurrency(personalData.income - personalData.expenses)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* All Transactions */}
      <Card className="card-premium">
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <CardTitle>All Transactions</CardTitle>
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full sm:w-48"
                />
              </div>
              {/* Filter */}
              <div className="flex bg-muted rounded-lg p-1">
                <button
                  onClick={() => setFilter('all')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${
                    filter === 'all' ? 'bg-background shadow-sm' : 'text-muted-foreground'
                  }`}
                >
                  <Layers className="w-3 h-3" /> All
                </button>
                <button
                  onClick={() => setFilter('business')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${
                    filter === 'business' ? 'bg-background shadow-sm text-emerald-500' : 'text-muted-foreground'
                  }`}
                >
                  <Briefcase className="w-3 h-3" /> Business
                </button>
                <button
                  onClick={() => setFilter('personal')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all flex items-center gap-1 ${
                    filter === 'personal' ? 'bg-background shadow-sm text-blue-500' : 'text-muted-foreground'
                  }`}
                >
                  <User className="w-3 h-3" /> Personal
                </button>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredTransactions.length > 0 ? (
            <div className="divide-y divide-border">
              {filteredTransactions.map((txn, idx) => {
                const isIncome = txn.amount > 0 || txn.entry_type === 'income';
                const isBusiness = txn.context === 'business';
                return (
                  <div key={txn.id || idx} className="flex items-center justify-between p-4 hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-4">
                      <div className={`p-2.5 rounded-xl ${isIncome ? 'bg-emerald-500/10' : 'bg-red-500/10'}`}>
                        {isIncome ? (
                          <ArrowUpRight className="w-5 h-5 text-emerald-500" />
                        ) : (
                          <ArrowDownRight className="w-5 h-5 text-red-500" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{txn.description || txn.vendor || 'Transaction'}</p>
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${isBusiness ? 'border-emerald-500/30 text-emerald-500' : 'border-blue-500/30 text-blue-500'}`}
                          >
                            {isBusiness ? 'Business' : 'Personal'}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span>{formatDate(txn.date)}</span>
                          {txn.category && (
                            <>
                              <span>•</span>
                              <span>{txn.category}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <span className={`text-lg font-semibold ${isIncome ? 'text-emerald-500' : 'text-red-500'}`}>
                      {isIncome ? '+' : '-'}{formatCurrency(Math.abs(txn.amount))}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-12 text-center text-muted-foreground">
              <Layers className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No transactions found</p>
              <p className="text-sm mt-1">Try adjusting your filters</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
